/**
 * Privacy Policy Page
 */
import { useEffect } from 'react';
import { Shield } from 'lucide-react';
import SEOHead from '../components/common/SEOHead';
import PublicHeader from '../components/common/PublicHeader';

const PrivacidadePage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Política de Privacidade — IMOCODE"
        description="Política de privacidade e proteção de dados da IMOCODE. Saiba como tratamos os dados pessoais de agentes imobiliários e leads capturados através dos nossos QR Codes."
        path="/privacidade"
      />
      <PublicHeader />

      {/* Content */}
      <main className="container mx-auto px-6 py-12 pt-28 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Política de Privacidade</h1>
              <p className="text-gray-500">IMOCODE</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-8">Última atualização: Março 2025</p>

          <p className="text-gray-600 mb-8">O IMOCODE respeita a privacidade dos seus utilizadores e cumpre o Regulamento Geral de Proteção de Dados (RGPD).</p>

          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Responsável pelo tratamento de dados</h2>
            <p className="text-gray-600 mb-4">O responsável pelo tratamento de dados é:</p>
            <p className="text-gray-800 font-medium mb-4">Cardume Pioneiro – Serviços Digitais Lda.</p>
            <p className="text-gray-600 mb-4">Contacto:</p>
            <p className="text-gray-600 mb-4">📧 <a href="mailto:suporte@imocode.app" className="text-blue-600 hover:underline">suporte@imocode.app</a></p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Dados recolhidos</h2>
            <p className="text-gray-600 mb-4">Podemos recolher os seguintes dados:</p>
            
            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Dados de registo</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>nome</li>
              <li>email</li>
              <li>telefone</li>
              <li>empresa (quando aplicável)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Dados de utilização</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>páginas visitadas</li>
              <li>QR Codes utilizados</li>
              <li>leads captadas</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-6 mb-3">Dados técnicos</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>endereço IP</li>
              <li>dispositivo utilizado</li>
              <li>navegador</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Finalidade do tratamento</h2>
            <p className="text-gray-600 mb-4">Os dados são utilizados para:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>prestação do serviço</li>
              <li>gestão de contas</li>
              <li>suporte ao utilizador</li>
              <li>melhoria da plataforma</li>
              <li>comunicações relacionadas com o serviço</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Base legal</h2>
            <p className="text-gray-600 mb-4">O tratamento de dados baseia-se em:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>execução de contrato</li>
              <li>interesse legítimo</li>
              <li>consentimento do utilizador</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Partilha de dados</h2>
            <p className="text-gray-600 mb-4">Os dados podem ser partilhados com:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>fornecedores de serviços tecnológicos</li>
              <li>plataformas de pagamento</li>
              <li>serviços de infraestrutura digital</li>
            </ul>
            <p className="text-gray-600 mb-4">Sempre em conformidade com o RGPD.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Conservação de dados</h2>
            <p className="text-gray-600 mb-4">Os dados são conservados apenas pelo tempo necessário para:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>prestação do serviço</li>
              <li>cumprimento de obrigações legais</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Direitos dos utilizadores</h2>
            <p className="text-gray-600 mb-4">Nos termos do RGPD, o utilizador tem direito a:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>acesso aos seus dados</li>
              <li>retificação</li>
              <li>apagamento</li>
              <li>limitação do tratamento</li>
              <li>portabilidade dos dados</li>
            </ul>
            <p className="text-gray-600 mb-4">Os pedidos podem ser enviados para:</p>
            <p className="text-gray-600 mb-4">📧 <a href="mailto:suporte@imocode.app" className="text-blue-600 hover:underline">suporte@imocode.app</a></p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Segurança</h2>
            <p className="text-gray-600 mb-4">O IMOCODE adota medidas técnicas e organizacionais adequadas para proteger os dados contra:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>acesso não autorizado</li>
              <li>perda</li>
              <li>alteração</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Cookies</h2>
            <p className="text-gray-600 mb-4">O website pode utilizar cookies para:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>melhorar a experiência do utilizador</li>
              <li>analisar tráfego</li>
              <li>garantir funcionamento técnico</li>
            </ul>
            <p className="text-gray-600 mb-4">O utilizador pode configurar o navegador para bloquear cookies.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Alterações à política</h2>
            <p className="text-gray-600 mb-4">Esta política pode ser atualizada periodicamente.</p>
            <p className="text-gray-600 mb-4">A versão mais recente estará sempre disponível no website.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Contacto</h2>
            <p className="text-gray-600 mb-4">Para qualquer questão relacionada com privacidade:</p>
            <p className="text-gray-600 mb-4">📧 <a href="mailto:suporte@imocode.app" className="text-blue-600 hover:underline">suporte@imocode.app</a></p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacidadePage;
